import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import api, { filesAPI, dtcAPI } from '../../services/api';
import { useDropzone } from 'react-dropzone';
import { 
  ChevronRight, ChevronLeft, Check, Upload, Car, Wrench, 
  Settings2, FileUp, ClipboardCheck, AlertCircle, Loader2, Cpu,
  Zap, Volume2, Gauge, Shield, Thermometer, Fuel, Eye, AlertTriangle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';

const STEPS = [
  { id: 1, name: 'Datei', icon: FileUp },
  { id: 2, name: 'Fahrzeug', icon: Car },
  { id: 3, name: 'Tool', icon: Wrench },
  { id: 4, name: 'Optionen', icon: Settings2 },
  { id: 5, name: 'Überprüfung', icon: ClipboardCheck },
];

const TOOL_TYPES = [
  { value: 'autotuner', label: 'Autotuner' },
  { value: 'kess_v2', label: 'KESS V2 / V3' },
  { value: 'ktag', label: 'KTag' },
  { value: 'cmd_flash', label: 'CMD Flash' },
  { value: 'flex', label: 'Magicmotorsport Flex' },
  { value: 'trasdata', label: 'Trasdata' },
  { value: 'pcmflash', label: 'PCMFlash' },
  { value: 'bitbox', label: 'BitBox' },
  { value: 'alientech', label: 'Alientech KESS3 / KESSv2' },
  { value: 'dimsport', label: 'DIMSport / NewGenius' },
  { value: 'mpps', label: 'MPPS' },
  { value: 'galletto', label: 'Galletto' },
  { value: 'foxflash', label: 'FoxFlash' },
  { value: 'bflash', label: 'BFlash' },
  { value: 'io_terminal', label: 'I/O Terminal' },
  { value: 'combiloader', label: 'Combiloader / PCMTuner' },
  { value: 'swiftec', label: 'Swiftec' },
  { value: 'ecuconnections', label: 'ECUConnections' },
  { value: 'byteshooter', label: 'Byteshooter' },
  { value: 'godiag', label: 'Godiag' },
  { value: 'other', label: 'Sonstiges' },
];

const READ_METHODS = [
  { value: 'obd', label: 'OBD (Diagnosebuchse)', desc: 'Auslesen über die OBD2-Buchse im Fahrzeug' },
  { value: 'bench', label: 'Bench (Tisch)', desc: 'Steuergerät ausgebaut, am Tisch angeschlossen' },
  { value: 'boot', label: 'Boot (BSL)', desc: 'Boot-Mode / TriCore BSL Zugang am Steuergerät' },
];

const CATEGORY_ICONS = {
  performance: Zap,
  emissions: Thermometer,
  sound: Volume2,
  system: Settings2,
  exhaust: Gauge,
  intake: Gauge,
  fuel: Fuel,
  sensors: Cpu,
  display: Eye,
  security: Shield,
};

const CATEGORY_NAMES = {
  performance: 'Leistung',
  emissions: 'Emissionen',
  sound: 'Sound',
  system: 'System',
  exhaust: 'Abgas',
  intake: 'Ansaugung',
  fuel: 'Kraftstoff',
  sensors: 'Sensoren',
  display: 'Anzeige',
  security: 'Sicherheit',
};

const CREDIT_PRICE = 60.0;

// Vehicle Step with database-powered dropdowns
const VehicleStep = ({ formData, setFormData, ecuDetection, t }) => {
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [brandSearch, setBrandSearch] = useState('');
  const [ecuInfo, setEcuInfo] = useState(null);
  const [loadingEcu, setLoadingEcu] = useState(false);

  useEffect(() => {
    api.get('/vehicles/brands').then(res => setBrands(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (formData.vehicle_brand) {
      api.get(`/vehicles/models/${encodeURIComponent(formData.vehicle_brand)}`).then(res => setModels(res.data)).catch(() => {});
    } else {
      setModels([]);
    }
  }, [formData.vehicle_brand]);

  // Fetch ECU info when brand + model are selected
  useEffect(() => {
    if (formData.vehicle_brand && formData.vehicle_model) {
      setLoadingEcu(true);
      api.get(`/vehicles/ecu-info/${encodeURIComponent(formData.vehicle_brand)}/${encodeURIComponent(formData.vehicle_model)}`)
        .then(res => {
          setEcuInfo(res.data);
          // Auto-fill ECU type if only one match and field is empty
          if (!formData.ecu_type && res.data.ecu_types?.length === 1) {
            setFormData(prev => ({ ...prev, ecu_type: res.data.ecu_types[0] }));
          }
        })
        .catch(() => setEcuInfo(null))
        .finally(() => setLoadingEcu(false));
    } else {
      setEcuInfo(null);
    }
  }, [formData.vehicle_brand, formData.vehicle_model]);

  const filteredBrands = brandSearch
    ? brands.filter(b => b.toLowerCase().includes(brandSearch.toLowerCase()))
    : brands;

  return (
    <div className="space-y-6" data-testid="step-vehicle">
      <h2 className="font-heading text-lg font-semibold text-white">Fahrzeugdaten eingeben</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="label-text">{t('upload.brand')} *</Label>
          <Select value={formData.vehicle_brand} onValueChange={(v) => setFormData(prev => ({ ...prev, vehicle_brand: v, vehicle_model: '' }))}>
            <SelectTrigger className="input-field" data-testid="vehicle-brand">
              <SelectValue placeholder="Marke wählen..." />
            </SelectTrigger>
            <SelectContent className="bg-[#23272F] border-white/10 max-h-72">
              <div className="p-2 sticky top-0 bg-[#23272F]">
                <Input placeholder="Suchen..." value={brandSearch} onChange={(e) => setBrandSearch(e.target.value)} className="input-field h-8 text-xs" />
              </div>
              {filteredBrands.slice(0, 50).map(b => (
                <SelectItem key={b} value={b}>{b}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="label-text">{t('upload.model')} *</Label>
          {models.length > 0 ? (
            <Select value={formData.vehicle_model} onValueChange={(v) => setFormData(prev => ({ ...prev, vehicle_model: v }))}>
              <SelectTrigger className="input-field" data-testid="vehicle-model">
                <SelectValue placeholder="Modell wählen..." />
              </SelectTrigger>
              <SelectContent className="bg-[#23272F] border-white/10 max-h-72">
                {models.map(m => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input value={formData.vehicle_model} onChange={(e) => setFormData(prev => ({ ...prev, vehicle_model: e.target.value }))} placeholder="z.B. 320d, A4, Golf" className="input-field" data-testid="vehicle-model" />
          )}
        </div>
        <div className="space-y-2">
          <Label className="label-text">{t('upload.year')} *</Label>
          <Input type="number" value={formData.vehicle_year} onChange={(e) => setFormData(prev => ({ ...prev, vehicle_year: parseInt(e.target.value) }))} min="1990" max={new Date().getFullYear() + 1} className="input-field" data-testid="vehicle-year" />
        </div>
        <div className="space-y-2">
          <Label className="label-text">{t('upload.engineCode')}</Label>
          <Input value={formData.engine_code} onChange={(e) => setFormData(prev => ({ ...prev, engine_code: e.target.value }))} placeholder="z.B. N47D20, CJCA" className="input-field" data-testid="engine-code" />
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label className="label-text">{t('upload.ecuType')} {ecuDetection?.ecu_type && '(automatisch erkannt)'}</Label>
          {ecuInfo?.ecu_types?.length > 0 ? (
            <Select value={formData.ecu_type} onValueChange={(v) => setFormData(prev => ({ ...prev, ecu_type: v }))}>
              <SelectTrigger className="input-field" data-testid="ecu-type">
                <SelectValue placeholder="ECU-Typ wählen..." />
              </SelectTrigger>
              <SelectContent className="bg-[#23272F] border-white/10 max-h-72">
                {ecuInfo.ecu_types.map(e => (
                  <SelectItem key={e} value={e}>{e}</SelectItem>
                ))}
                <SelectItem value="__custom">Manuell eingeben...</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <Input value={formData.ecu_type} onChange={(e) => setFormData(prev => ({ ...prev, ecu_type: e.target.value }))} placeholder="z.B. Bosch EDC17C46, Siemens PCR2.1" className="input-field" data-testid="ecu-type" />
          )}
          {formData.ecu_type === '__custom' && (
            <Input value="" onChange={(e) => setFormData(prev => ({ ...prev, ecu_type: e.target.value }))} placeholder="ECU-Typ manuell eingeben..." className="input-field mt-2" autoFocus />
          )}
        </div>

        {/* New Fields */}
        <div className="space-y-2">
          <Label className="label-text">Kennzeichen</Label>
          <Input value={formData.license_plate} onChange={(e) => setFormData(prev => ({ ...prev, license_plate: e.target.value }))} placeholder="z.B. HB-TX 123" className="input-field" data-testid="license-plate" />
        </div>
        <div className="space-y-2">
          <Label className="label-text">Getriebe</Label>
          <Select value={formData.transmission_type} onValueChange={(v) => setFormData(prev => ({ ...prev, transmission_type: v }))}>
            <SelectTrigger className="input-field" data-testid="transmission-type">
              <SelectValue placeholder="Getriebeart wählen..." />
            </SelectTrigger>
            <SelectContent className="bg-[#23272F] border-white/10">
              <SelectItem value="manual">Schaltgetriebe (Manuell)</SelectItem>
              <SelectItem value="automatic">Automatik</SelectItem>
              <SelectItem value="dsg">DSG / Doppelkupplung</SelectItem>
              <SelectItem value="cvt">CVT / Stufenlos</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="label-text">Dateityp</Label>
          <Select value={formData.file_type} onValueChange={(v) => setFormData(prev => ({ ...prev, file_type: v }))}>
            <SelectTrigger className="input-field" data-testid="file-type">
              <SelectValue placeholder="Dateityp wählen..." />
            </SelectTrigger>
            <SelectContent className="bg-[#23272F] border-white/10">
              <SelectItem value="ecu">ECU File (Motorsteuergerät)</SelectItem>
              <SelectItem value="tcu">TCU File (Getriebesteuergerät)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="label-text">Dateistatus</Label>
          <Select value={formData.file_state} onValueChange={(v) => setFormData(prev => ({ ...prev, file_state: v }))}>
            <SelectTrigger className="input-field" data-testid="file-state">
              <SelectValue placeholder="Status wählen..." />
            </SelectTrigger>
            <SelectContent className="bg-[#23272F] border-white/10">
              <SelectItem value="original">Original (Seriendatei)</SelectItem>
              <SelectItem value="tuned">Bereits getuned</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* ECU Info Panel - shows available methods and data from scraped sources */}
      {loadingEcu && (
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin" /> ECU-Daten werden geladen...
        </div>
      )}
      {ecuInfo && ecuInfo.total_matches > 0 && !loadingEcu && (
        <div className="p-4 bg-emerald-500/5 border border-emerald-500/20 rounded-lg space-y-3" data-testid="ecu-info-panel">
          <div className="flex items-center gap-2 text-sm font-medium text-emerald-400">
            <Cpu className="w-4 h-4" />
            {ecuInfo.total_matches} ECU-Datensätze gefunden für {ecuInfo.brand} {ecuInfo.model}
          </div>
          
          {ecuInfo.methods?.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-slate-400">Verfügbare Methoden:</span>
              {ecuInfo.methods.map(m => (
                <span key={m} className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  {m}
                </span>
              ))}
            </div>
          )}
          
          {ecuInfo.engines?.length > 0 && ecuInfo.engines.length <= 10 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-slate-400">Motoren:</span>
              {ecuInfo.engines.map(e => (
                <span key={e} className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-white/5 text-slate-300 border border-white/10">
                  {e}
                </span>
              ))}
            </div>
          )}

          {ecuInfo.details?.length > 0 && ecuInfo.details.length <= 8 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-slate-500">
                    <th className="text-left p-1">ECU</th>
                    <th className="text-left p-1">Motor</th>
                    <th className="text-left p-1">Methode</th>
                    <th className="text-left p-1">Tool</th>
                    <th className="text-left p-1">Quelle</th>
                  </tr>
                </thead>
                <tbody>
                  {ecuInfo.details.slice(0, 8).map((d, i) => (
                    <tr key={i} className="border-t border-white/5">
                      <td className="p-1 text-white font-mono">{d.ecu || '-'}</td>
                      <td className="p-1 text-slate-300">{d.engine || '-'}</td>
                      <td className="p-1 text-emerald-400">{d.method || '-'}</td>
                      <td className="p-1 text-slate-400">{d.tool || '-'}</td>
                      <td className="p-1 text-slate-500">{d.source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const UploadPage = () => {
  const { user, refreshBalance } = useAuth();
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [tuningOptions, setTuningOptions] = useState({});
  const [ecuDetection, setEcuDetection] = useState(null);
  const [ecuVehicleMatches, setEcuVehicleMatches] = useState([]);
  const [selectedVehicleMatch, setSelectedVehicleMatch] = useState(null);
  const [dtcSuggestions, setDtcSuggestions] = useState([]);
  const [selectedDtcs, setSelectedDtcs] = useState([]);
  
  const [formData, setFormData] = useState({
    vehicle_brand: searchParams.get('brand') || '',
    vehicle_model: searchParams.get('model') || '',
    vehicle_year: parseInt(searchParams.get('year')) || new Date().getFullYear(),
    engine_code: '',
    ecu_type: '',
    license_plate: '',
    transmission_type: '',
    file_type: 'ecu',
    file_state: 'original',
    tool_type: '',
    tool_serial: '',
    read_method: '',
    base_tuning: searchParams.get('options')?.split(',').find(o => ['stage1','stage2','eco_tuning'].includes(o)) || '',
    options: searchParams.get('options')?.split(',').filter(o => !['stage1','stage2','eco_tuning',''].includes(o)) || [],
    notes: '',
    file: null,
    originalLog: null,
  });

  useEffect(() => {
    // Fetch tuning options from API
    const fetchOptions = async () => {
      try {
        const response = await api.get('/tuning-options');
        setTuningOptions(response.data.by_category || {});
      } catch (err) {
        console.error('Failed to fetch tuning options:', err);
      }
    };
    fetchOptions();
  }, []);

  const analyzeFile = async (file) => {
    setAnalyzing(true);
    setEcuVehicleMatches([]);
    setSelectedVehicleMatch(null);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await api.post('/files/analyze-ecu', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const { analysis, matching_vehicles } = response.data;
      
      setEcuDetection(analysis);
      setEcuVehicleMatches(matching_vehicles || []);
      
      // Auto-fill detected ECU type
      if (analysis.ecu_type) {
        setFormData(prev => ({
          ...prev,
          ecu_type: analysis.ecu_type,
          file_type: analysis.is_tcu ? 'tcu' : 'ecu',
        }));
      }
    } catch (err) {
      console.error('File analysis failed:', err);
      // Fallback to legacy analyzer
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        const response = await api.post('/analyze-file', formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setEcuDetection(response.data);
        if (response.data.ecu_type) {
          setFormData(prev => ({
            ...prev,
            ecu_type: `${response.data.ecu_brand || ''} ${response.data.ecu_type}`.trim()
          }));
        }
      } catch (err2) {
        console.error('Legacy analysis also failed:', err2);
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const selectVehicleMatch = (vehicle) => {
    setSelectedVehicleMatch(vehicle);
    setFormData(prev => ({
      ...prev,
      vehicle_brand: vehicle.brand,
      vehicle_model: vehicle.model,
      vehicle_version: vehicle.version || '',
      engine_code: vehicle.engine_type || '',
      ecu_type: vehicle.ecu_model || prev.ecu_type,
      vehicle_engine: vehicle.engine || '',
      vehicle_power_ps: vehicle.power_ps || '',
      vehicle_fuel: vehicle.fuel || '',
    }));
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setFormData(prev => ({ ...prev, file }));
      analyzeFile(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 1,
    accept: {
      'application/octet-stream': ['.bin', '.ori', '.mod', '.hex', '.ecu', '.fls', '.bdm'],
    },
  });

  const calculateCredits = () => {
    let totalCredits = 0;
    // Add base tuning credits
    const allOptions = [...(formData.base_tuning ? [formData.base_tuning] : []), ...formData.options];
    for (const optId of allOptions) {
      for (const category of Object.values(tuningOptions)) {
        const opt = category.find(o => o.id === optId);
        if (opt) {
          totalCredits += opt.credits;
          break;
        }
      }
    }
    return totalCredits;
  };

  const getAllSelectedOptions = () => {
    return [...(formData.base_tuning ? [formData.base_tuning] : []), ...formData.options];
  };

  const calculatePrice = () => {
    return calculateCredits() * CREDIT_PRICE;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const handleOptionToggle = (optionId) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.includes(optionId)
        ? prev.options.filter(o => o !== optionId)
        : [...prev.options, optionId]
    }));

    // Fetch DTC suggestions when dtc_off is selected
    if (optionId === 'dtc_off' && !formData.options.includes('dtc_off') && ecuDetection?.ecu_type) {
      dtcAPI.suggestions(ecuDetection.ecu_type).then(res => {
        setDtcSuggestions(res.data || []);
      }).catch(() => {});
    }
    if (optionId === 'dtc_off' && formData.options.includes('dtc_off')) {
      setDtcSuggestions([]);
      setSelectedDtcs([]);
    }
  };

  const validateStep = () => {
    switch (currentStep) {
      case 1:
        return formData.file;
      case 2:
        return formData.vehicle_brand && formData.vehicle_model && formData.vehicle_year;
      case 3:
        return formData.tool_type && formData.read_method;
      case 4:
        return formData.base_tuning !== '';
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    const totalCredits = calculateCredits();
    
    if ((user?.balance || 0) < totalCredits) {
      setError(`Unzureichendes Guthaben. Sie benötigen ${totalCredits.toFixed(1)} Credits.`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('file', formData.file);
      if (formData.originalLog) {
        formDataToSend.append('original_log', formData.originalLog);
      }
      formDataToSend.append('vehicle_brand', formData.vehicle_brand);
      formDataToSend.append('vehicle_model', `${formData.vehicle_model} ${formData.vehicle_version || ''}`.trim());
      formDataToSend.append('vehicle_year', formData.vehicle_year.toString());
      formDataToSend.append('engine_code', formData.engine_code || '');
      formDataToSend.append('ecu_type', formData.ecu_type || '');
      formDataToSend.append('tool_type', formData.tool_type);
      formDataToSend.append('read_method', formData.read_method);
      formDataToSend.append('tool_serial', formData.tool_serial || '');
      formDataToSend.append('options', JSON.stringify(getAllSelectedOptions()));
      formDataToSend.append('notes', formData.notes || '');
      formDataToSend.append('license_plate', formData.license_plate || '');
      formDataToSend.append('transmission_type', formData.transmission_type || '');
      formDataToSend.append('file_type', formData.file_type || 'ecu');
      formDataToSend.append('file_state', formData.file_state || 'original');
      if (selectedDtcs.length > 0) {
        formDataToSend.append('dtc_codes', JSON.stringify(selectedDtcs));
      }

      const response = await filesAPI.upload(formDataToSend);
      
      setSuccess({
        ticketId: response.data.ticket_id,
        queuePosition: response.data.queue_position,
        price: response.data.price,
      });
      
      await refreshBalance();
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload fehlgeschlagen. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  };

  // Success State
  if (success) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto" data-testid="upload-success">
          <div className="glass-card p-8 text-center">
            <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-emerald-500" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-white mb-2">
              Datei erfolgreich hochgeladen!
            </h1>
            <p className="text-slate-400 mb-6">
              Ihre Datei wurde in die Warteschlange aufgenommen.
            </p>
            
            <div className="glass-card p-6 mb-6 text-left">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Ticket-ID</p>
                  <p className="font-mono text-lg text-[#EC2229]">{success.ticketId}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Warteschlange</p>
                  <p className="font-mono text-lg text-white">Position #{success.queuePosition}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Credits</p>
                  <p className="font-mono text-lg text-white">{calculateCredits()} Credits</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Preis</p>
                  <p className="font-mono text-lg text-white">{formatCurrency(success.price)}</p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => navigate('/files')} className="btn-secondary">
                Zu meinen Dateien
              </Button>
              <Button onClick={() => { setSuccess(null); setCurrentStep(1); setFormData(prev => ({ ...prev, file: null, base_tuning: '', options: [] })); setEcuDetection(null); setEcuVehicleMatches([]); setSelectedVehicleMatch(null); }} className="btn-primary">
                Weitere Datei hochladen
              </Button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto" data-testid="upload-wizard">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-2xl font-bold text-white">{t('upload.title')}</h1>
          <p className="text-slate-400 mt-1">Laden Sie Ihre ECU-Datei hoch und wählen Sie Ihre Tuning-Optionen</p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between relative">
            <div className="absolute left-0 right-0 top-1/2 h-0.5 bg-white/10 -translate-y-1/2" />
            <div className="absolute left-0 top-1/2 h-0.5 bg-[#EC2229] -translate-y-1/2 transition-all duration-300" style={{ width: `${((currentStep - 1) / (STEPS.length - 1)) * 100}%` }} />
            
            {STEPS.map((step) => (
              <div key={step.id} className="relative z-10 flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${currentStep > step.id ? 'bg-[#EC2229] text-white' : currentStep === step.id ? 'bg-[#EC2229] text-white ring-4 ring-[#EC2229]/30' : 'bg-[#23272F] text-slate-500 border border-white/10'}`}>
                  {currentStep > step.id ? <Check className="w-5 h-5" /> : <step.icon className="w-5 h-5" />}
                </div>
                <span className={`mt-2 text-xs font-medium hidden sm:block ${currentStep >= step.id ? 'text-white' : 'text-slate-500'}`}>{step.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}

        {/* Step Content */}
        <div className="glass-card p-6 mb-6">
          {/* Step 1: File Upload with ECU Detection & Vehicle Matching */}
          {currentStep === 1 && (
            <div className="space-y-6" data-testid="step-file">
              <h2 className="font-heading text-lg font-semibold text-white">ECU-Datei hochladen</h2>
              
              <div {...getRootProps()} className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all ${isDragActive ? 'border-[#EC2229] bg-[#EC2229]/10' : formData.file ? 'border-emerald-500 bg-emerald-500/10' : 'border-white/20 hover:border-white/40'}`}>
                <input {...getInputProps()} data-testid="file-input" />
                
                {formData.file ? (
                  <div className="space-y-2">
                    <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto">
                      <Check className="w-6 h-6 text-emerald-500" />
                    </div>
                    <p className="text-white font-medium">{formData.file.name}</p>
                    <p className="text-sm text-slate-400">{(formData.file.size / 1024 / 1024).toFixed(2)} MB</p>
                    <p className="text-xs text-slate-500">Klicken um zu ändern</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="w-12 h-12 text-slate-500 mx-auto" />
                    <p className="text-white">{t('upload.dropFile')}</p>
                    <p className="text-sm text-slate-500">Unterstützte Formate: .bin, .ori, .mod, .hex, .ecu, .fls, .bdm</p>
                  </div>
                )}
              </div>

              {/* ECU Detection Result */}
              {analyzing && (
                <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl flex items-center gap-3">
                  <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                  <span className="text-blue-400">Analysiere Datei & suche passende Fahrzeuge...</span>
                </div>
              )}
              
              {/* OLSX-style Two-Column: File Info + Vehicle Matching */}
              {ecuDetection && !analyzing && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="ecu-analysis-result">
                  {/* Left Column: Informationen aus dem File */}
                  <div className="glass-card p-5 lg:col-span-1">
                    <h3 className="font-semibold text-white mb-4 flex items-center gap-2 text-sm">
                      <Cpu className="w-4 h-4 text-[#EC2229]" />
                      Informationen aus dem File
                    </h3>
                    <div className="space-y-3 text-sm">
                      {ecuDetection.vehicle_manufacturer && (
                        <div>
                          <p className="text-slate-500 text-xs">Hersteller</p>
                          <p className="text-white font-semibold">{ecuDetection.vehicle_manufacturer}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-slate-500 text-xs">Steuergerät</p>
                        <p className="text-white font-semibold">
                          <span className="text-[#EC2229] font-bold">{ecuDetection.ecu_maker || '?'}</span>{' '}
                          {ecuDetection.ecu_type || 'Unbekannt'}
                        </p>
                      </div>
                      {ecuDetection.sw_version && (
                        <div>
                          <p className="text-slate-500 text-xs">Software-Version</p>
                          <p className="text-white font-mono text-xs">{ecuDetection.sw_version}</p>
                        </div>
                      )}
                      {ecuDetection.hw_version && (
                        <div>
                          <p className="text-slate-500 text-xs">Hardware-Version</p>
                          <p className="text-white font-mono text-xs">{ecuDetection.hw_version}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-slate-500 text-xs">Kraftstoff</p>
                        <p className="text-white">{ecuDetection.fuel_type || 'Unbekannt'}</p>
                      </div>
                      {ecuDetection.is_tcu && (
                        <div className="p-2 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                          <p className="text-amber-400 text-xs font-medium">Getriebesteuergerät (TCU)</p>
                        </div>
                      )}
                      <div>
                        <p className="text-slate-500 text-xs">Dateigröße</p>
                        <p className="text-white">{(ecuDetection.file_size / 1024 / 1024).toFixed(1)} MB</p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs">Erkennungsrate</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                            <div className="h-full bg-[#EC2229] rounded-full" style={{ width: `${ecuDetection.confidence || 0}%` }} />
                          </div>
                          <span className="text-xs text-slate-400">{ecuDetection.confidence || 0}%</span>
                        </div>
                      </div>
                      {ecuDetection.identifiers?.length > 0 && (
                        <div className="pt-2 border-t border-white/[0.06]">
                          <p className="text-slate-500 text-xs mb-1">Erkannte Merkmale</p>
                          <div className="space-y-1">
                            {ecuDetection.identifiers.slice(0, 5).map((id, i) => (
                              <p key={i} className="text-[10px] text-slate-400 font-mono truncate">{id}</p>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right Column: Ihr Fahrzeug - Vehicle Matches */}
                  <div className="lg:col-span-2 space-y-4">
                    <h3 className="font-semibold text-white flex items-center gap-2 text-sm">
                      <Car className="w-4 h-4 text-[#EC2229]" />
                      Ihr Fahrzeug
                      {ecuVehicleMatches.length > 0 && (
                        <span className="text-xs text-slate-400 font-normal">({ecuVehicleMatches.length} Treffer)</span>
                      )}
                    </h3>

                    {ecuVehicleMatches.length > 0 ? (
                      <>
                        {/* Vehicle Match Cards - show ALL matches */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2 max-h-[480px] overflow-y-auto pr-1 scrollbar-thin">
                          {ecuVehicleMatches.map((v, i) => {
                            const isSelected = selectedVehicleMatch &&
                              selectedVehicleMatch.brand === v.brand &&
                              selectedVehicleMatch.model === v.model &&
                              selectedVehicleMatch.engine === v.engine;
                            return (
                              <button
                                key={i}
                                onClick={() => selectVehicleMatch(v)}
                                className={`p-3 rounded-xl border text-left transition-all ${
                                  isSelected
                                    ? 'bg-[#EC2229]/10 border-[#EC2229]/50 ring-2 ring-[#EC2229]/30'
                                    : 'bg-white/[0.03] border-white/[0.06] hover:border-white/[0.15]'
                                }`}
                                data-testid={`vehicle-match-${i}`}
                              >
                                <p className="text-white font-semibold text-sm truncate">
                                  {v.brand} {v.model}
                                </p>
                                <p className="text-xs text-slate-400 truncate">{v.version}</p>
                                <p className="text-xs text-[#EC2229] mt-1 truncate">
                                  {v.engine} — {v.power_ps}PS ({v.power_kw}kW)
                                </p>
                                <p className="text-[10px] text-slate-500 mt-0.5">{v.fuel} | {v.ecu_model}</p>
                              </button>
                            );
                          })}
                        </div>
                        {selectedVehicleMatch && (
                          <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg flex items-center gap-2">
                            <Check className="w-4 h-4 text-emerald-500" />
                            <span className="text-sm text-emerald-400">
                              Ausgewählt: <strong className="text-white">{selectedVehicleMatch.brand} {selectedVehicleMatch.model} {selectedVehicleMatch.version}</strong> — {selectedVehicleMatch.engine} {selectedVehicleMatch.power_ps}PS
                            </span>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="p-6 bg-white/[0.03] border border-white/[0.06] rounded-xl text-center">
                        <AlertCircle className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                        <p className="text-slate-400 text-sm">Keine automatische Zuordnung möglich</p>
                        <p className="text-xs text-slate-500 mt-1">Bitte wählen Sie das Fahrzeug manuell in Schritt 2</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Original Log Upload (Optional) */}
              <div className="space-y-2">
                <Label className="label-text">Original Log (optional)</Label>
                <p className="text-xs text-slate-500">Falls vorhanden, laden Sie die Original-Logdatei (Datenlog / Messdatei) hoch</p>
                <div className="relative">
                  <input
                    type="file"
                    onChange={(e) => {
                      if (e.target.files?.[0]) {
                        setFormData(prev => ({ ...prev, originalLog: e.target.files[0] }));
                      }
                    }}
                    className="hidden"
                    id="original-log-input"
                    data-testid="original-log-input"
                  />
                  <label
                    htmlFor="original-log-input"
                    className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${formData.originalLog ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-white/10 hover:border-white/20 bg-white/5'}`}
                  >
                    <FileUp className={`w-5 h-5 ${formData.originalLog ? 'text-emerald-500' : 'text-slate-500'}`} />
                    {formData.originalLog ? (
                      <div className="flex-1 flex items-center justify-between">
                        <div>
                          <p className="text-white text-sm font-medium">{formData.originalLog.name}</p>
                          <p className="text-xs text-slate-400">{(formData.originalLog.size / 1024).toFixed(1)} KB</p>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); setFormData(prev => ({ ...prev, originalLog: null })); }}
                          className="text-slate-500 hover:text-red-400 text-xs"
                        >
                          Entfernen
                        </button>
                      </div>
                    ) : (
                      <span className="text-sm text-slate-400">Original Log auswählen...</span>
                    )}
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Vehicle */}
          {currentStep === 2 && (
            <VehicleStep formData={formData} setFormData={setFormData} ecuDetection={ecuDetection} t={t} />
          )}

          {/* Step 3: Tool & Read Method */}
          {currentStep === 3 && (
            <div className="space-y-6" data-testid="step-tool">
              <h2 className="font-heading text-lg font-semibold text-white">Auslesetool & Methode</h2>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="label-text">{t('upload.selectTool')} *</Label>
                  <Select value={formData.tool_type} onValueChange={(value) => setFormData(prev => ({ ...prev, tool_type: value }))}>
                    <SelectTrigger className="input-field" data-testid="tool-type">
                      <SelectValue placeholder="Tool auswählen..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[#23272F] border-white/10 max-h-72">
                      {TOOL_TYPES.map(tool => (
                        <SelectItem key={tool.value} value={tool.value}>{tool.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="label-text">Auslesemethode *</Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {READ_METHODS.map(method => (
                      <button
                        key={method.value}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, read_method: method.value }))}
                        className={`p-4 rounded-lg border text-left transition-all ${formData.read_method === method.value ? 'bg-[#EC2229]/10 border-[#EC2229]/50 ring-1 ring-[#EC2229]/30' : 'bg-white/5 border-white/10 hover:border-white/20'}`}
                        data-testid={`read-method-${method.value}`}
                      >
                        <p className={`font-semibold text-sm ${formData.read_method === method.value ? 'text-[#EC2229]' : 'text-white'}`}>{method.label}</p>
                        <p className="text-xs text-slate-400 mt-1">{method.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="label-text">{t('upload.toolSerial')}</Label>
                  <Input value={formData.tool_serial} onChange={(e) => setFormData(prev => ({ ...prev, tool_serial: e.target.value }))} placeholder="Optional: Seriennummer des Tools" className="input-field" data-testid="tool-serial" />
                </div>

                {formData.tool_type === 'other' && (
                  <div className="space-y-2">
                    <Label className="label-text">Tool-Name</Label>
                    <Input value={formData.tool_other || ''} onChange={(e) => setFormData(prev => ({ ...prev, tool_other: e.target.value }))} placeholder="Name des verwendeten Tools" className="input-field" data-testid="tool-other" />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 4: Options - Staged: First base tuning, then add-ons */}
          {currentStep === 4 && (
            <div className="space-y-6" data-testid="step-options">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-lg font-semibold text-white">{t('upload.selectOptions')}</h2>
                <div className="text-right">
                  <p className="text-sm text-slate-400">Gewählte Credits</p>
                  <p className="text-xl font-bold text-[#EC2229]">{calculateCredits().toFixed(1)} Credits</p>
                </div>
              </div>

              {/* Fuel type info */}
              {ecuDetection?.fuel_type && ecuDetection.fuel_type !== 'Mixed' && (
                <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg flex items-center gap-2 text-sm">
                  <Fuel className="w-4 h-4 text-blue-400" />
                  <span className="text-blue-300">
                    Kraftstofftyp erkannt: <strong className="text-white">{ecuDetection.fuel_type}</strong>
                  </span>
                </div>
              )}

              {/* Stage 1: Base Tuning Selection */}
              <div className="space-y-3">
                <h3 className="font-medium text-white flex items-center gap-2">
                  <Zap className="w-4 h-4 text-[#EC2229]" />
                  Schritt 1: Basis-Tuning wählen *
                </h3>
                <p className="text-xs text-slate-400">Wählen Sie zunächst Ihr Basis-Tuning aus.</p>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  {/* Serienleistung option (stock - 0 credits) */}
                  <button
                    onClick={() => setFormData(prev => ({ ...prev, base_tuning: 'stock' }))}
                    className={`p-4 rounded-xl border text-left transition-all ${
                      formData.base_tuning === 'stock'
                        ? 'bg-[#EC2229]/10 border-[#EC2229]/50 ring-2 ring-[#EC2229]/30'
                        : 'bg-white/5 border-white/10 hover:border-white/20'
                    }`}
                    data-testid="base-tuning-stock"
                  >
                    <div className="flex items-center justify-between mb-1">
                      {formData.base_tuning === 'stock' && <Check className="w-4 h-4 text-[#EC2229]" />}
                      <span className="text-white font-semibold">Serienleistung</span>
                      <span className="text-slate-500 font-mono text-sm">0 Cr</span>
                    </div>
                    <p className="text-[10px] text-slate-500 mt-1">Nur Zusatzoptionen</p>
                  </button>
                  {(() => {
                    const baseIds = ['stage1', 'stage2', 'eco_tuning'];
                    const baseOptions = [];
                    for (const cat of Object.values(tuningOptions)) {
                      for (const opt of cat) {
                        if (baseIds.includes(opt.id)) baseOptions.push(opt);
                      }
                    }
                    return baseOptions.map(opt => (
                      <button
                        key={opt.id}
                        onClick={() => setFormData(prev => ({ ...prev, base_tuning: opt.id }))}
                        className={`p-4 rounded-xl border text-left transition-all ${
                          formData.base_tuning === opt.id
                            ? 'bg-[#EC2229]/10 border-[#EC2229]/50 ring-2 ring-[#EC2229]/30'
                            : 'bg-white/5 border-white/10 hover:border-white/20'
                        }`}
                        data-testid={`base-tuning-${opt.id}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          {formData.base_tuning === opt.id && <Check className="w-4 h-4 text-[#EC2229]" />}
                          <span className="text-white font-semibold">{opt.name}</span>
                          <span className="text-[#EC2229] font-mono text-sm font-bold">{opt.credits} Cr</span>
                        </div>
                      </button>
                    ));
                  })()}
                </div>
              </div>
              
              {/* Stage 2: Add-on Options (only shown after base tuning is selected) */}
              {formData.base_tuning && (
                <div className="space-y-4 pt-4 border-t border-white/[0.06]">
                  <h3 className="font-medium text-white flex items-center gap-2">
                    <Settings2 className="w-4 h-4 text-[#EC2229]" />
                    Schritt 2: Zusatzoptionen wählen (optional)
                  </h3>
                  <p className="text-xs text-slate-400">Wählen Sie zusätzliche Tuning-Optionen zu Ihrem Basis-Tuning.</p>

                  {Object.entries(tuningOptions).map(([category, options]) => {
                    const CategoryIcon = CATEGORY_ICONS[category] || Settings2;
                    const addonOptions = options.filter(o => !['stage1', 'stage2', 'eco_tuning'].includes(o.id));
                    if (addonOptions.length === 0) return null;
                    return (
                      <div key={category} className="space-y-2">
                        <h4 className="text-sm font-medium text-slate-400 flex items-center gap-2">
                          <CategoryIcon className="w-3.5 h-3.5 text-[#EC2229]" />
                          {CATEGORY_NAMES[category] || category}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                          {addonOptions.map(opt => (
                            <button
                              key={opt.id}
                              onClick={() => handleOptionToggle(opt.id)}
                              className={`p-3 rounded-lg border text-left transition-all text-sm ${formData.options.includes(opt.id) ? 'bg-[#EC2229]/10 border-[#EC2229]/50 ring-1 ring-[#EC2229]/30' : 'bg-white/5 border-white/10 hover:border-white/20'}`}
                              data-testid={`option-${opt.id}`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  {formData.options.includes(opt.id) && <Check className="w-3.5 h-3.5 text-[#EC2229]" />}
                                  <span className="text-white font-medium truncate pr-2">{opt.name}</span>
                                </div>
                                <span className="text-[#EC2229] font-mono text-xs whitespace-nowrap">{opt.credits} Cr</span>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* DTC Suggestion Panel - shows when dtc_off is selected */}
              {formData.options.includes('dtc_off') && dtcSuggestions.length > 0 && (
                <div className="glass-card p-5 border-amber-500/20 bg-amber-500/5" data-testid="dtc-suggestions-panel">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                    <h3 className="font-medium text-white text-sm">
                      DTC-Vorschlaege fuer {ecuDetection?.ecu_brand} {ecuDetection?.ecu_type}
                    </h3>
                  </div>
                  <p className="text-xs text-slate-400 mb-3">
                    Waehlen Sie die DTC-Codes aus, die deaktiviert werden sollen. Diese werden als Hinweis an den Tuner gesendet.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 max-h-48 overflow-y-auto pr-1">
                    {dtcSuggestions.map(dtc => (
                      <button
                        key={dtc.code}
                        type="button"
                        onClick={() => setSelectedDtcs(prev =>
                          prev.includes(dtc.code)
                            ? prev.filter(c => c !== dtc.code)
                            : [...prev, dtc.code]
                        )}
                        className={`flex items-center gap-2 p-2 rounded text-left text-xs transition-all ${
                          selectedDtcs.includes(dtc.code)
                            ? 'bg-[#EC2229]/10 border border-[#EC2229]/40'
                            : 'bg-white/5 border border-white/5 hover:border-white/15'
                        }`}
                        data-testid={`dtc-suggestion-${dtc.code}`}
                      >
                        <span className={`font-mono font-medium ${selectedDtcs.includes(dtc.code) ? 'text-[#EC2229]' : 'text-slate-300'}`}>
                          {dtc.code}
                        </span>
                        <span className="text-slate-400 truncate flex-1">{dtc.description?.de || dtc.description?.en}</span>
                        <span className={`px-1 py-0.5 rounded text-[10px] ${
                          dtc.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                          dtc.severity === 'warning' ? 'bg-amber-500/20 text-amber-400' :
                          'bg-slate-500/20 text-slate-400'
                        }`}>{dtc.system}</span>
                      </button>
                    ))}
                  </div>
                  {selectedDtcs.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between">
                      <span className="text-xs text-slate-400">{selectedDtcs.length} DTC-Code(s) ausgewaehlt</span>
                      <button
                        type="button"
                        onClick={() => setSelectedDtcs([])}
                        className="text-xs text-slate-500 hover:text-white"
                      >
                        Alle abwaehlen
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label className="label-text">{t('upload.notes')}</Label>
                <Textarea value={formData.notes} onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))} placeholder="Optionale Hinweise oder Wünsche..." className="input-field min-h-[80px]" data-testid="notes" />
              </div>
            </div>
          )}

          {/* Step 5: Review */}
          {currentStep === 5 && (
            <div className="space-y-6" data-testid="step-review">
              <h2 className="font-heading text-lg font-semibold text-white">Zusammenfassung</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="glass-card p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Datei</p>
                  <p className="text-white font-mono text-sm truncate">{formData.file?.name}</p>
                  <p className="text-sm text-slate-400">{(formData.file?.size / 1024 / 1024).toFixed(2)} MB</p>
                  {ecuDetection?.ecu_type && (
                    <p className="text-sm text-[#EC2229] mt-1">{ecuDetection.ecu_maker} {ecuDetection.ecu_type}</p>
                  )}
                  {formData.originalLog && (
                    <p className="text-xs text-slate-400 mt-2">+ Original Log: {formData.originalLog.name}</p>
                  )}
                </div>

                <div className="glass-card p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Fahrzeug</p>
                  <p className="text-white font-medium">{formData.vehicle_brand} {formData.vehicle_model} {formData.vehicle_version || ''}</p>
                  <p className="text-sm text-slate-400">{formData.vehicle_year}</p>
                  {formData.license_plate && <p className="text-xs text-slate-500 mt-1">Kennzeichen: {formData.license_plate}</p>}
                  {formData.transmission_type && <p className="text-xs text-slate-500">Getriebe: {formData.transmission_type}</p>}
                </div>

                <div className="glass-card p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Tool & Methode</p>
                  <p className="text-white font-medium">{TOOL_TYPES.find(t => t.value === formData.tool_type)?.label}</p>
                  <p className="text-sm text-[#EC2229] mt-0.5">{READ_METHODS.find(m => m.value === formData.read_method)?.label}</p>
                  {formData.tool_serial && <p className="text-xs text-slate-500 mt-0.5">SN: {formData.tool_serial}</p>}
                </div>

                <div className="glass-card p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Tuning</p>
                  {formData.base_tuning && <span className="badge-neutral text-xs bg-[#EC2229]/10 text-[#EC2229] border-[#EC2229]/30 mr-1">{formData.base_tuning}</span>}
                  <div className="flex flex-wrap gap-1 mt-1">
                    {formData.options.slice(0, 4).map(opt => (
                      <span key={opt} className="badge-neutral text-xs">{opt}</span>
                    ))}
                    {formData.options.length > 4 && (
                      <span className="text-xs text-slate-500">+{formData.options.length - 4} weitere</span>
                    )}
                  </div>
                  <p className="text-[10px] text-slate-500 mt-1">{formData.file_type === 'tcu' ? 'TCU (Getriebe)' : 'ECU (Motor)'} — {formData.file_state === 'tuned' ? 'Bereits getuned' : 'Original'}</p>
                </div>
              </div>

              {/* Price Summary */}
              <div className="glass-card p-6 bg-[#EC2229]/5 border-[#EC2229]/20">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Gesamtkosten</p>
                    <p className="text-3xl font-heading font-bold text-white">{calculateCredits().toFixed(1)} <span className="text-lg text-slate-400">Credits</span></p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Ihr Guthaben</p>
                    <p className={`text-xl font-mono ${(user?.balance || 0) >= calculateCredits() ? 'text-emerald-500' : 'text-red-500'}`}>
                      {(user?.balance || 0).toFixed(1)} Credits
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button variant="outline" onClick={handleBack} disabled={currentStep === 1} className="btn-secondary" data-testid="btn-back">
            <ChevronLeft className="w-4 h-4 mr-2" />
            {t('upload.back')}
          </Button>

          {currentStep < 5 ? (
            <Button onClick={handleNext} disabled={!validateStep()} className="btn-primary" data-testid="btn-next">
              {t('upload.next')}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={loading || (user?.balance || 0) < calculateCredits()} className="btn-primary" data-testid="btn-submit">
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('common.loading')}
                </>
              ) : (
                <>
                  {t('upload.submit')}
                  <Check className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default UploadPage;
